// Zod mirrors of surveys-core's recipe.rs validation (generic-surveys spec
// §3): one schema per step kind's config plus the list-level rules, run at
// submit time (the Steps section) so it can show its own errors before the
// PUT round-trips to the server for the same answer. Keep this file's rules
// in lockstep with crates/surveys-core/src/recipe.rs — that module is what
// the server actually enforces; this is a best-effort preview of it.
//
// This file also owns the steps form shape (StepsFormValues) and the
// mappings to/from the wire — the same split template-schema.ts makes for
// the template editor.

import { z } from "zod";
import type { PutStep } from "@/api/surveys";
import type { ChoiceConfig, ChoiceOption, MultiConfig, MultiOption, StepKind, StepRow, TextConfig } from "@/api/types";
import { matchingPreset, presetByKey } from "@/features/presets/scale-presets";
import type { DraftStep } from "@/features/preview/draft";
import { BUILTIN_ICON_NAMES } from "./choice-icons";

/** surveys-core's MAX_STEPS. */
export const MAX_STEPS = 20;

/** surveys-core's option-count bound, shared by a choice step's options and
 *  a multi step's option set(s). */
export const MAX_OPTIONS = 10;

/** surveys-core's option_key_ok: keys name answers in exports, filters and
 *  email links, so they are identifiers, not labels. */
export const OPTION_KEY_PATTERN = /^[a-z0-9_-]{1,40}$/;
const OPTION_KEY_MESSAGE = "Keys are 1-40 characters of a-z, 0-9, _ and -";

const optionKeySchema = z
    .string()
    .trim()
    .min(1, "Every option needs a key")
    .regex(OPTION_KEY_PATTERN, OPTION_KEY_MESSAGE);

// ── Wire-shape schemas ──────────────────────────────────────────────────────

function uniqueKeys(items: { key: string }[]): boolean {
    return new Set(items.map((item) => item.key)).size === items.length;
}

export const choiceOptionSchema = z
    .object({
        key: optionKeySchema,
        label: z.string().trim().nullable().optional(),
        icon: z
            .string()
            .nullable()
            .optional()
            .refine((icon) => icon == null || (BUILTIN_ICON_NAMES as readonly string[]).includes(icon), {
                message: "Unknown icon",
            }),
        assetId: z.string().nullable().optional(),
        assetUrl: z.string().nullable().optional(),
        value: z.number().nullable().optional(),
    })
    .refine((option) => Boolean(option.label || option.icon || option.assetId), {
        message: "Needs a label, icon, or image",
        path: ["label"],
    });

export const choiceConfigSchema = z
    .object({
        prompt: z.string().trim().min(1, "Prompt is required"),
        description: z.string().trim().nullable().optional(),
        preset: z.string().nullable().optional(),
        options: z
            .array(choiceOptionSchema)
            .min(2, "A choice step needs at least 2 options")
            .max(MAX_OPTIONS, `A choice step allows at most ${MAX_OPTIONS} options`),
    })
    .refine((config) => uniqueKeys(config.options), { message: "Option keys must be unique", path: ["options"] });

/** The scored step's options must every one carry a value (recipe.rs
 *  MetricOptionWithoutValue). */
export const metricChoiceConfigSchema = choiceConfigSchema.refine(
    (config) => config.options.every((option) => typeof option.value === "number"),
    { message: "Every option of the scored step needs a value", path: ["options"] },
);

export const multiOptionSchema = z.object({
    key: optionKeySchema,
    label: z.string().trim().min(1, "Every option needs a label"),
});

const multiOptionsSchema = z
    .array(multiOptionSchema)
    .min(1, "Needs at least one option")
    .max(MAX_OPTIONS, `At most ${MAX_OPTIONS} options`)
    .refine(uniqueKeys, { message: "Option keys must be unique" });

export const flatMultiConfigSchema = z.object({
    mode: z.literal("flat"),
    prompt: z.string().trim().min(1, "Prompt is required"),
    description: z.string().trim().nullable().optional(),
    options: multiOptionsSchema,
});

export const splitMultiConfigSchema = z.object({
    mode: z.literal("split"),
    promptNegative: z.string().trim().min(1, "Prompt is required"),
    promptPositive: z.string().trim().min(1, "Prompt is required"),
    descriptionNegative: z.string().trim().nullable().optional(),
    descriptionPositive: z.string().trim().nullable().optional(),
    negative: multiOptionsSchema,
    positive: multiOptionsSchema,
    threshold: z.number(),
});

export const multiConfigSchema = z.discriminatedUnion("mode", [flatMultiConfigSchema, splitMultiConfigSchema]);

export const textConfigSchema = z.object({
    prompt: z.string().trim().min(1, "Prompt is required"),
    description: z.string().trim().nullable().optional(),
    multiline: z.boolean().optional(),
    placeholder: z.string().trim().nullable().optional(),
});

// ── Steps editor form shape ─────────────────────────────────────────────────

export interface OptionFormValue {
    key: string;
    label: string;
    icon: string;
    assetId: string;
    assetUrl: string;
    value: number;
}

export interface MultiOptionFormValue {
    key: string;
    label: string;
}

export type MultiMode = "flat" | "split";

/** One step's row in the editor. Every kind's fields are present on every
 *  row so react-hook-form paths stay static; `kind` decides which are read
 *  when the row goes to the wire. */
export interface StepFormValue {
    /** The persisted StepRow id, or null for a step added in this session.
     *  Named `stepId`, not `id`: useFieldArray stamps its own `id` key on
     *  every row and would clobber ours. */
    stepId: string | null;
    kind: StepKind;
    isMetric: boolean;
    /** No Skip in the widget; a split multi step only when it is shown. */
    required: boolean;
    /** The choice prompt, the flat multi prompt, or the text prompt. */
    prompt: string;
    /** The optional line under `prompt`; "" for none. */
    description: string;
    options: OptionFormValue[];
    multiMode: MultiMode;
    multiOptions: MultiOptionFormValue[];
    promptNegative: string;
    promptPositive: string;
    descriptionNegative: string;
    descriptionPositive: string;
    negative: MultiOptionFormValue[];
    positive: MultiOptionFormValue[];
    threshold: number;
    /** Text steps: a text box, or a single-line field when false. */
    multiline: boolean;
    placeholder: string;
}

export interface StepsFormValues {
    steps: StepFormValue[];
}

/** A brand-new choice step starts from this scale — a friendly default
 *  rather than an empty (and unsavable) table. */
const DEFAULT_PRESET_KEY = "smiley-5";

function toOptionFormValue(option: ChoiceOption): OptionFormValue {
    return {
        key: option.key,
        label: option.label ?? "",
        icon: option.icon ?? "",
        assetId: option.assetId ?? "",
        assetUrl: option.assetUrl ?? "",
        value: option.value ?? 0,
    };
}

function toMultiOptionFormValue(option: MultiOption): MultiOptionFormValue {
    return { key: option.key, label: option.label };
}

function defaultOptions(): OptionFormValue[] {
    const preset = presetByKey(DEFAULT_PRESET_KEY);
    return preset ? preset.options.map(toOptionFormValue) : [];
}

function blankStep(kind: StepKind): StepFormValue {
    return {
        stepId: null,
        kind,
        isMetric: false,
        required: false,
        prompt: "",
        description: "",
        options: kind === "choice" ? defaultOptions() : [],
        multiMode: "flat",
        multiOptions:
            kind === "multi"
                ? [
                      { key: "option-1", label: "" },
                      { key: "option-2", label: "" },
                  ]
                : [],
        promptNegative: "",
        promptPositive: "",
        descriptionNegative: "",
        descriptionPositive: "",
        negative: [],
        positive: [],
        threshold: 0,
        multiline: true,
        placeholder: "",
    };
}

/** A new step of `kind`, ready to edit. */
export function newStep(kind: StepKind): StepFormValue {
    return blankStep(kind);
}

/** One stored row as an editor row. A config that fails to parse (should
 *  not happen; the server validated it) degrades to a blank row of the same
 *  kind rather than crashing the page. */
export function toStepFormValue(row: StepRow): StepFormValue {
    const step: StepFormValue = {
        ...blankStep(row.kind),
        stepId: row.id,
        isMetric: row.isMetric,
        required: row.required,
    };
    if (row.kind === "choice") {
        const parsed = choiceConfigSchema.safeParse(row.config);
        if (parsed.success) {
            step.prompt = parsed.data.prompt;
            step.description = parsed.data.description ?? "";
            step.options = parsed.data.options.map(toOptionFormValue);
        }
    } else if (row.kind === "multi") {
        const parsed = multiConfigSchema.safeParse(row.config);
        if (parsed.success) {
            if (parsed.data.mode === "flat") {
                step.multiMode = "flat";
                step.prompt = parsed.data.prompt;
                step.description = parsed.data.description ?? "";
                step.multiOptions = parsed.data.options.map(toMultiOptionFormValue);
            } else {
                step.multiMode = "split";
                step.promptNegative = parsed.data.promptNegative;
                step.promptPositive = parsed.data.promptPositive;
                step.descriptionNegative = parsed.data.descriptionNegative ?? "";
                step.descriptionPositive = parsed.data.descriptionPositive ?? "";
                step.negative = parsed.data.negative.map(toMultiOptionFormValue);
                step.positive = parsed.data.positive.map(toMultiOptionFormValue);
                step.threshold = parsed.data.threshold;
            }
        }
    } else {
        const parsed = textConfigSchema.safeParse(row.config);
        if (parsed.success) {
            step.prompt = parsed.data.prompt;
            step.description = parsed.data.description ?? "";
            step.multiline = parsed.data.multiline ?? true;
            step.placeholder = parsed.data.placeholder ?? "";
        }
    }
    return step;
}

/** The editor's defaults from a survey's stored steps (possibly empty). */
export function toStepsDefaults(steps: StepRow[]): StepsFormValues {
    return { steps: [...steps].sort((a, b) => a.position - b.position).map(toStepFormValue) };
}

// ── To the wire ─────────────────────────────────────────────────────────────

/** One option row as the wire sees it: the form's "" sentinel for "unset"
 *  becomes `undefined`, matching how a preset's own entries omit the field.
 *  `value` travels unconditionally, scored step or not. `ChoiceOption.value`
 *  is only ever *enforced* server-side when the step is the survey's metric
 *  (recipe.rs's MetricOptionWithoutValue); an unscored step's widget just
 *  ignores it. Dropping it on save used to be lossy: on reload
 *  `toOptionFormValue` reads a missing value back as `0`, so every unscored
 *  option's value collapsed to 0 — silently re-scaling the step (to an
 *  all-zero scale) the moment it was later turned into the score, and
 *  confusing `matchingPreset`, which compares values, into reporting
 *  "Custom" for what was really still a named preset. */
export function optionFormToChoiceOption(option: OptionFormValue): ChoiceOption {
    return {
        key: option.key.trim(),
        label: option.label.trim() || undefined,
        icon: option.icon || undefined,
        assetId: option.assetId || undefined,
        assetUrl: option.assetId ? option.assetUrl || undefined : undefined,
        value: option.value,
    };
}

export function toChoiceConfig(step: StepFormValue): ChoiceConfig {
    const wireOptions = step.options.map((option) => optionFormToChoiceOption(option));
    // Preset matching compares values, which now always travel — so this
    // matches on the same wire rows regardless of `isMetric`.
    const preset = matchingPreset(wireOptions)?.key ?? null;
    return {
        prompt: step.prompt.trim(),
        description: step.description.trim() || undefined,
        preset,
        options: wireOptions,
    };
}

function trimmedOptions(items: MultiOptionFormValue[]): MultiOption[] {
    return items.map((item) => ({ key: item.key.trim(), label: item.label.trim() }));
}

export function toMultiConfig(step: StepFormValue): MultiConfig {
    if (step.multiMode === "flat") {
        return {
            mode: "flat",
            prompt: step.prompt.trim(),
            description: step.description.trim() || undefined,
            options: trimmedOptions(step.multiOptions),
        };
    }
    return {
        mode: "split",
        promptNegative: step.promptNegative.trim(),
        promptPositive: step.promptPositive.trim(),
        descriptionNegative: step.descriptionNegative.trim() || undefined,
        descriptionPositive: step.descriptionPositive.trim() || undefined,
        negative: trimmedOptions(step.negative),
        positive: trimmedOptions(step.positive),
        threshold: step.threshold,
    };
}

export function toTextConfig(step: StepFormValue): TextConfig {
    return {
        prompt: step.prompt.trim(),
        description: step.description.trim() || undefined,
        // Stored only when off: absent is the text box, the widget's default.
        multiline: step.multiline ? undefined : false,
        placeholder: step.placeholder.trim() || undefined,
    };
}

/** A step config as the wire carries it. The config interfaces have no
 *  index signature, so lining them up with `Record<string, unknown>` needs
 *  this (unknown-mediated, not `any`) hop. */
function wire(config: ChoiceConfig | MultiConfig | TextConfig): Record<string, unknown> {
    return config as unknown as Record<string, unknown>;
}

export function toStepConfig(step: StepFormValue): Record<string, unknown> {
    if (step.kind === "choice") return wire(toChoiceConfig(step));
    if (step.kind === "multi") return wire(toMultiConfig(step));
    return wire(toTextConfig(step));
}

/** The PUT /steps body: order is position; an existing row keeps its id. */
export function toPutSteps(values: StepsFormValues): PutStep[] {
    return values.steps.map((step) => ({
        ...(step.stepId ? { id: step.stepId } : {}),
        kind: step.kind,
        isMetric: step.isMetric,
        required: step.required,
        config: toStepConfig(step),
    }));
}

/** The live form as the preview's step list: the same wire shapes a save
 *  assembles, minus validation (a half-typed step still previews) and asset
 *  resolution (the preview panel resolves option artwork itself). */
export function toDraftSteps(values: StepsFormValues): DraftStep[] {
    return values.steps.map((step) => ({
        kind: step.kind,
        isMetric: step.isMetric,
        required: step.required,
        config: toStepConfig(step),
    }));
}

/** Index of the scored step, or -1. */
export function metricIndex(values: StepsFormValues): number {
    return values.steps.findIndex((step) => step.isMetric && step.kind === "choice");
}

/** The scored step's distinct option values, ascending — what a split
 *  multi's threshold select offers. Empty when there is no scored step. */
export function metricValues(values: StepsFormValues): number[] {
    const index = metricIndex(values);
    const metric = index >= 0 ? values.steps[index] : undefined;
    if (!metric) return [];
    return Array.from(new Set(metric.options.map((option) => option.value))).sort((a, b) => a - b);
}

// ── Validation ──────────────────────────────────────────────────────────────

export interface StepsProblem {
    /** The offending step's index, or null for a rule about the whole list. */
    index: number | null;
    message: string;
}

/** validateSteps' message for a split multi-select with no score before it. */
export const SPLIT_NEEDS_SCORE = "A split multi-select needs a scored choice step before it";

function firstIssue(result: { success: boolean; error?: z.ZodError }): string | null {
    if (result.success) return null;
    return result.error?.issues[0]?.message ?? "This step needs attention";
}

/** Mirrors surveys-core's validate_steps: the list rules, then each step's
 *  config for its kind. Returns the first problem found, or null. */
export function validateSteps(values: StepsFormValues): StepsProblem | null {
    const steps = values.steps;
    if (steps.length > MAX_STEPS) return { index: null, message: `A survey allows at most ${MAX_STEPS} steps` };
    const metricIndexes = steps.map((step, index) => (step.isMetric ? index : -1)).filter((index) => index >= 0);
    // Reported on the second scored step so the editor can point at a card;
    // the server treats this as a list-level rule.
    if (metricIndexes.length > 1) return { index: metricIndexes[1] ?? null, message: "Only one step can be the score" };
    const metricAt = metricIndexes[0] ?? -1;

    for (let index = 0; index < steps.length; index++) {
        const step = steps[index] as StepFormValue;
        if (step.isMetric && step.kind !== "choice") {
            return { index, message: "Only a choice step can be the score" };
        }
        const config = toStepConfig(step);
        let message: string | null;
        if (step.kind === "choice") {
            message = firstIssue((step.isMetric ? metricChoiceConfigSchema : choiceConfigSchema).safeParse(config));
        } else if (step.kind === "multi") {
            message = firstIssue(multiConfigSchema.safeParse(config));
            if (message === null && step.multiMode === "split" && !(metricAt >= 0 && metricAt < index)) {
                message = SPLIT_NEEDS_SCORE;
            }
        } else {
            message = firstIssue(textConfigSchema.safeParse(config));
        }
        if (message !== null) return { index, message };
    }
    return null;
}
